import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FolderKanban,
  Plus,
  Search,
  Star,
  Users,
  Calendar,
  CheckCircle2,
  Clock,
  MoreVertical,
  Archive,
  Trash2,
  Edit,
  Folder,
  X,
} from 'lucide-react';
import { MainLayout } from '../../components/MainLayout';
import api from '../../api/client';
import ProjectCreateModal from '../../components/tasks/ProjectCreateModal';

export default function ProjectsApp() {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const filter = searchParams.get('filter') || 'all'; // all, my-projects, shared-projects, archived

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/projects', {
        params: {
          filter,
          search: searchQuery.trim() || undefined,
        },
      });
      setProjects(res.data || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, searchQuery]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleToggleFavorite = async (e, p) => {
    e.stopPropagation();
    try {
      await api.post(`/projects/${p.id}/favorite`);
      fetchProjects();
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleArchive = async (e, p) => {
    e.stopPropagation();
    try {
      await api.post(`/projects/${p.id}/archive`);
      fetchProjects();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to toggle archive');
    }
  };

  const handleDeleteProject = async (e, p) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete workspace "${p.name}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/projects/${p.id}`);
      fetchProjects();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete project');
    }
  };

  const getTabTitle = () => {
    switch (filter) {
      case 'my-projects': return 'My Projects';
      case 'shared-projects': return 'Shared Projects';
      case 'archived': return 'Archived Projects';
      default: return 'All Projects';
    }
  };

  return (
    <MainLayout>
      {/* Top Header - Full Bleed Edge-to-Edge */}
      <div className="projects-hero-header notes-hero-banner">
        <div className="notes-hero-content">
          <div>
            <h1 className="notes-hero-title">
              {getTabTitle()}
            </h1>
            <p className="notes-hero-subtitle">
              Collaborative workspaces for milestones, sprints, tasks, and deliverables.
            </p>
          </div>

          <div className="projects-hero-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => {
                setEditingProject(null);
                setCreateModalOpen(true);
              }}
              className="btn-primary notes-hero-btn-primary projects-new-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <Plus size={16} />
              <span>New Project</span>
            </button>
            <button
              type="button"
              onClick={() => setSearchOpen(!searchOpen)}
              className={`projects-search-toggle-btn ${searchOpen ? 'active' : ''}`}
              title="Search projects"
              aria-label="Toggle search input"
            >
              <Search size={16} />
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1600px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Expandable Search Bar on Mobile / Desktop */}
        {searchOpen && (
          <div className="projects-search-expand-bar animate-fade-in">
            <Search size={16} color="var(--text-secondary)" style={{ flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Search projects by name, description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="clear-search-btn"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {/* Filter Toolbar with Horizontal Scrollable Tabs */}
        <div className="projects-filter-toolbar">
          <div className="projects-tabs-scroll">
            {[
              { id: 'all', label: 'All Projects', path: '/apps/tasks/projects' },
              { id: 'my-projects', label: 'My Projects', path: '/apps/tasks/projects?filter=my-projects' },
              { id: 'shared-projects', label: 'Shared Projects', path: '/apps/tasks/projects?filter=shared-projects' },
              { id: 'archived', label: 'Archived', path: '/apps/tasks/projects?filter=archived' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => navigate(tab.path)}
                className={filter === tab.id ? 'btn-primary' : 'btn-secondary'}
                style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px', flexShrink: 0 }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Loading projects...
          </div>
        ) : projects.length === 0 ? (
          <div
            style={{
              padding: '64px 24px',
              textAlign: 'center',
              background: 'var(--bg-surface)',
              borderRadius: '16px',
              border: '1px dashed var(--border-color)',
            }}
          >
            <FolderKanban size={48} color="var(--text-secondary)" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: '16px', color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
              No projects found
            </h3>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 20px 0' }}>
              {filter === 'archived' ? 'No archived projects.' : 'Create your first project to organize tasks and collaborate.'}
            </p>
            {filter !== 'archived' && (
              <button
                onClick={() => {
                  setEditingProject(null);
                  setCreateModalOpen(true);
                }}
                className="btn-primary"
              >
                Create Project
              </button>
            )}
          </div>
        ) : (
          <div className="projects-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
            {projects.map((p) => (
              <div
                key={p.id}
                className="project-card"
                onClick={() => navigate(`/apps/tasks/projects/${p.id}`)}
                style={{
                  background: 'var(--bg-surface)',
                  borderRadius: '16px',
                  border: '1px solid var(--border-color)',
                  padding: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                  transition: 'transform 0.15s, border-color 0.15s, box-shadow 0.15s',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.borderColor = 'var(--accent-primary)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                }}
              >
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        padding: '10px',
                        borderRadius: '10px',
                        background: 'rgba(59, 130, 246, 0.15)',
                        color: 'var(--accent-primary)',
                      }}
                    >
                      <FolderKanban size={20} />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {p.name}
                      </h3>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                        {p.visibility} Workspace
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => handleToggleFavorite(e, p)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}
                    >
                      <Star size={17} fill={p.is_favorite ? '#eab308' : 'none'} color={p.is_favorite ? '#eab308' : 'var(--text-secondary)'} />
                    </button>
                    <button
                      onClick={(e) => handleToggleArchive(e, p)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-secondary)' }}
                      title={p.archived_at ? 'Unarchive' : 'Archive'}
                    >
                      <Archive size={16} />
                    </button>
                    <button
                      onClick={(e) => handleDeleteProject(e, p)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--danger)' }}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Description */}
                {p.description && (
                  <p
                    style={{
                      margin: 0,
                      fontSize: '13px',
                      color: 'var(--text-secondary)',
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {p.description}
                  </p>
                )}

                {/* Status & Priority Pills */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 600,
                      background:
                        p.status === 'COMPLETED'
                          ? 'rgba(16, 185, 129, 0.2)'
                          : p.status === 'IN_PROGRESS'
                          ? 'rgba(6, 182, 212, 0.2)'
                          : 'rgba(255, 255, 255, 0.08)',
                      color:
                        p.status === 'COMPLETED'
                          ? 'var(--success)'
                          : p.status === 'IN_PROGRESS'
                          ? 'var(--accent-primary)'
                          : 'var(--text-secondary)',
                    }}
                  >
                    {p.status}
                  </span>

                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 600,
                      background:
                        p.priority === 'URGENT'
                          ? 'rgba(239, 68, 68, 0.2)'
                          : p.priority === 'HIGH'
                          ? 'rgba(245, 158, 11, 0.2)'
                          : 'rgba(59, 130, 246, 0.15)',
                      color:
                        p.priority === 'URGENT'
                          ? 'var(--danger)'
                          : p.priority === 'HIGH'
                          ? 'var(--warning)'
                          : 'var(--accent-primary)',
                    }}
                  >
                    {p.priority}
                  </span>
                </div>

                {/* Progress Bar */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    <span>Progress ({p.completed_tasks}/{p.total_tasks} tasks)</span>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{p.progress}%</span>
                  </div>
                  <div style={{ height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${p.progress}%`,
                        background: p.progress === 100 ? 'var(--success)' : 'var(--accent-primary)',
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>
                </div>

                {/* Footer metadata */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: '10px',
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    fontSize: '12px',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Users size={14} />
                    <span>{p.member_count} {p.member_count === 1 ? 'member' : 'members'}</span>
                  </div>

                  {p.end_at && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Calendar size={14} />
                      <span>Due {new Date(p.end_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ProjectCreateModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => fetchProjects()}
        initialProject={editingProject}
      />
    </MainLayout>
  );
}
